import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { subscribeQueueEvents, type QueueBusEvent } from '../../lib/queueBus';
import type { Office, OfficeAnalytics } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { ErrorNote, Spinner, StatusBadge } from '../../ui/components';

const OFFICE_POLL_MS = 5_000;
const ANALYTICS_POLL_MS = 20_000;

/**
 * Full-screen "now serving" board for a lobby TV. c10-servicedesk has no
 * GET endpoint to list/poll live queue state (see src/lib/queueBus.ts for
 * why), so the "now serving" number here is driven by a same-browser
 * BroadcastChannel relay from the Counter Console, not a server poll. Office
 * info and (for OFFICE_MANAGER personas) analytics ARE real server polls,
 * refreshed on a timer via useApiQuery's reload().
 */
export function TvBoard() {
  const { officeCode = '' } = useParams();
  const { persona } = useAuth();
  const isManager = persona?.roles.includes('OFFICE_MANAGER') ?? false;

  const officeQ = useApiQuery<Office>(officeCode ? `/api/v1/servicedesk/offices/${officeCode}` : null);
  const analyticsQ = useApiQuery<OfficeAnalytics>(
    isManager && officeCode ? `/api/v1/servicedesk/offices/${officeCode}/analytics` : null,
  );

  useEffect(() => {
    const t = setInterval(() => officeQ.reload(), OFFICE_POLL_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [officeCode]);

  useEffect(() => {
    if (!isManager) return;
    const t = setInterval(() => analyticsQ.reload(), ANALYTICS_POLL_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [officeCode, isManager]);

  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1_000);
    return () => clearInterval(t);
  }, []);

  const [nowServing, setNowServing] = useState<QueueBusEvent | null>(null);
  const [recent, setRecent] = useState<QueueBusEvent[]>([]);

  useEffect(() => {
    return subscribeQueueEvents((evt) => {
      if (evt.officeCode !== officeCode) return;
      setRecent((r) => [evt, ...r].slice(0, 6));
      if (evt.status === 'CALLED') setNowServing(evt);
    });
  }, [officeCode]);

  if (officeQ.error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-ink-950 text-white">
        <ErrorNote message={officeQ.error} />
        <Link to="/tv" className="text-sm font-semibold text-brand-100 underline">
          ← Choose another office
        </Link>
      </div>
    );
  }

  if (officeQ.loading || !officeQ.data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink-950">
        <Spinner label="Loading office…" />
      </div>
    );
  }

  const office = officeQ.data;

  return (
    <div className="flex min-h-screen flex-col bg-ink-950 px-10 py-8 text-white">
      <Link to="/tv" className="absolute right-6 top-6 text-2xl text-slate-600 hover:text-slate-300" aria-label="Change office">
        ✕
      </Link>

      <header className="flex items-baseline justify-between">
        <div>
          <div className="text-3xl font-black tracking-tight">{office.name}</div>
          <div className="text-sm text-slate-400">
            {office.island.replaceAll('_', ' ')} · {office.district}
          </div>
        </div>
        <div className="text-right">
          <div className="text-4xl font-black tabular-nums">{now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</div>
          <div className="text-sm text-slate-400">{now.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}</div>
        </div>
      </header>

      <div className="flex flex-1 flex-col items-center justify-center">
        <div className="text-lg font-semibold uppercase tracking-[0.3em] text-slate-400">Now serving</div>
        {nowServing ? (
          <>
            <div className="mt-4 text-[10rem] font-black leading-none tabular-nums text-white">{nowServing.number}</div>
            {nowServing.priority !== 'NORMAL' ? (
              <div className="mt-2">
                <StatusBadge status={nowServing.priority} />
              </div>
            ) : null}
          </>
        ) : (
          <div className="mt-6 text-3xl font-semibold text-slate-600">— · —</div>
        )}
      </div>

      {recent.length > 1 ? (
        <div className="mb-2 flex items-center justify-center gap-3 text-sm text-slate-500">
          <span className="font-semibold uppercase tracking-wide text-slate-600">Recently called</span>
          {recent.slice(1, 6).map((e, i) => (
            <span key={`${e.ticketId}-${i}`} className="tabular-nums">
              {e.number}
            </span>
          ))}
        </div>
      ) : null}

      <footer className="flex items-center justify-center gap-8 border-t border-ink-800 pt-4 text-sm text-slate-400">
        <span>{office.counters} counter{office.counters === 1 ? '' : 's'}</span>
        {analyticsQ.data ? (
          <>
            <span>Avg wait {analyticsQ.data.averageWaitMinutes !== null ? `${analyticsQ.data.averageWaitMinutes} min` : '—'}</span>
            <span>{analyticsQ.data.appointmentsToday} appointments today</span>
          </>
        ) : null}
      </footer>
    </div>
  );
}
